if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          thumbnailList: '[id^="Slider-Thumbnails"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.initialized = false;
        this.activeMediaSignature = null;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.destroySlider();
        this.destroyImageZoom();
        this.mediaQuery?.removeEventListener?.('change', this.handleMediaQueryChange);
        if (this.mediaQuery && !this.mediaQuery.removeEventListener) this.mediaQuery.onchange = null;
      }

      init() {
        if (this.initialized) return;

        this.materializeDeferredMedia();
        this.refreshElements();
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.galleryMap = this.parseGalleryMap();
        this.captureCanonicalMedia();
        this.activeMediaCount = this.canonicalMedia.length;
        this.initialized = true;
        this.setSliderOptions();

        this.mediaQuery = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        this.handleMediaQueryChange = this.updateMediaLayout.bind(this);
        if (this.mediaQuery.addEventListener) {
          this.mediaQuery.addEventListener('change', this.handleMediaQueryChange);
        } else {
          this.mediaQuery.onchange = this.handleMediaQueryChange;
        }
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      refreshElements() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
      }

      materializeDeferredMedia() {
        const deferredSlides = this.querySelector('[id^="DeferredSlides"]');
        const deferredThumbnails = this.querySelector('[id^="DeferredThumbnails"]');
        const mediaList = this.querySelector('[id^="Slider-Gallery"]');
        const thumbnailList = this.querySelector('[id^="Slider-Thumbnails"]');

        if (deferredSlides?.content && mediaList) {
          mediaList.appendChild(deferredSlides.content.cloneNode(true));
          deferredSlides.remove();
        }
        if (deferredThumbnails?.content && thumbnailList) {
          thumbnailList.appendChild(deferredThumbnails.content.cloneNode(true));
          deferredThumbnails.remove();
        }
      }

      parseGalleryMap() {
        const source = this.querySelector('[data-variant-gallery-map]');
        if (!source) return { colorOptionPosition: 1, currentMarketHandle: '', groups: [] };

        try {
          const parsed = JSON.parse(source.textContent || '{}');
          return {
            colorOptionPosition: Number(parsed.colorOptionPosition) || 1,
            currentMarketHandle: String(parsed.currentMarketHandle || '')
              .trim()
              .toLowerCase(),
            groups: Array.isArray(parsed.groups) ? parsed.groups : [],
          };
        } catch (error) {
          console.error('Variant gallery mapping JSON is invalid; using the full product gallery.', error);
          return { colorOptionPosition: 1, currentMarketHandle: '', groups: [] };
        }
      }

      captureCanonicalMedia() {
        this.canonicalMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')].map(
          (node) => node.cloneNode(true)
        );
        this.canonicalThumbnails = [...this.querySelectorAll('.product__thumbs-item:not(.swiper-slide-duplicate)')].map(
          (node) => node.cloneNode(true)
        );
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: this.activeMediaCount > 1,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (!this.initialized || this.activeMediaCount < 2) {
          this.destroySlider();
          return;
        }
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (
          this.activeMediaCount > 1 &&
          this.elements.viewer &&
          this.elements.mediaList &&
          typeof this.sliderInstance !== 'object'
        ) {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 0,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      updateSlider() {
        this.destroySlider();
        this.destroyImageZoom();
        this.refreshElements();
        this.activeMediaCount = this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)').length;
        this.setSliderOptions();
        this.initSlider();
        if (this.enableImageZoom) this.initImageZoom();
      }

      destroySlider() {
        if (this.sliderInstance?.slider && !this.sliderInstance.slider.destroyed) {
          this.sliderInstance.slider.destroy(true, true);
        }
        if (this.thumbsInstance?.slider && !this.thumbsInstance.slider.destroyed) {
          this.thumbsInstance.slider.destroy(true, true);
        }
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        this.destroyImageZoom();
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                if (!media.querySelector('product-model')) break;
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                if (!media.querySelector('video-element')) break;
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        if (!dataSource.length) return;

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          const currIndex = this.lightbox?.pswp?.currIndex;

          if (Number.isInteger(currIndex) && this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideToLoop(currIndex, 0, false);
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        this.zoomDelegateCleanup = FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      destroyImageZoom() {
        this.zoomDelegateCleanup?.();
        this.zoomDelegateCleanup = null;
        if (this.lightbox) {
          try {
            this.lightbox.pswp?.close();
            this.lightbox.destroy();
          } catch (error) {
            console.warn('Unable to fully destroy the product media lightbox.', error);
          }
          this.lightbox = null;
        }
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
          //初始化显示第一张图片
          setTimeout(()=>{
            if (this.sliderInstance?.slider && !this.sliderInstance.slider.destroyed) {
              this.sliderInstance.slider.slideToLoop(0, 0, false);
            }
          }, 100);
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs?.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance?.slider && this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant, selectedOptionValueIds = []) {
        this.init();
        if (!variant) return;

        const selection = this.resolveGallerySelection(variant, selectedOptionValueIds);
        if (this.activeMediaSignature === null && selection.signature === 'fallback') {
          this.activeMediaSignature = 'fallback';
        } else if (selection.signature !== this.activeMediaSignature) {
          this.applyMediaSet(selection.mediaIds, selection.signature);
        }

        const firstActiveMediaId = this.querySelector('.product__media-item:not(.swiper-slide-duplicate)')?.dataset
          .mediaId;
        const targetMediaId =
          selection.signature === 'fallback' ? variant.featured_media?.id || firstActiveMediaId : firstActiveMediaId;
        this.focusMedia(targetMediaId);
      }

      resolveGallerySelection(variant, selectedOptionValueIds) {
        const groups = this.galleryMap?.groups || [];
        const variantId = String(variant.id);
        const colorIndex = Math.max(0, (this.galleryMap?.colorOptionPosition || 1) - 1);
        const colorOptionValueId = String(selectedOptionValueIds[colorIndex] || '');
        const currentMarketHandle = String(this.galleryMap?.currentMarketHandle || '')
          .trim()
          .toLowerCase();
        const groupMarketHandle = (group) => String(group.marketHandle || '').trim().toLowerCase();
        const isCurrentMarketGroup = (group) => {
          const marketHandle = groupMarketHandle(group);
          return Boolean(currentMarketHandle) && marketHandle !== 'global' && marketHandle === currentMarketHandle;
        };
        const isGlobalGroup = (group) => {
          const marketHandle = groupMarketHandle(group);
          return !marketHandle || marketHandle === 'global';
        };
        const matchesVariant = (group) =>
          group.mode === 'variant' && String(group.variantId || '') === variantId;
        const matchesColor = (group) =>
          group.mode === 'color' && String(group.colorOptionValueId || '') === colorOptionValueId;
        const candidates = [
          ...groups.filter((group) => isCurrentMarketGroup(group) && matchesVariant(group)),
          ...groups.filter((group) => isCurrentMarketGroup(group) && matchesColor(group)),
          ...groups.filter((group) => isGlobalGroup(group) && matchesVariant(group)),
          ...groups.filter((group) => isGlobalGroup(group) && matchesColor(group)),
        ];
        const availableIds = new Set(this.canonicalMedia.map((node) => String(node.dataset.mediaId)));
        for (const matchedGroup of candidates) {
          const mediaIds = [...new Set((matchedGroup.mediaIds || []).map(String))].filter((id) =>
            availableIds.has(id)
          );
          if (mediaIds.length) {
            const marketHandle = groupMarketHandle(matchedGroup) || 'global';
            return {
              mediaIds,
              signature: `${marketHandle}:${matchedGroup.mode}:${
                matchedGroup.variantId || matchedGroup.colorOptionValueId
              }:${mediaIds.join(',')}`,
            };
          }

          const warningKey = `${groupMarketHandle(matchedGroup) || 'global'}:${matchedGroup.mode}:${
            matchedGroup.variantId || matchedGroup.colorOptionValueId
          }`;
          if (this.lastInvalidMappingWarning !== warningKey) {
            console.warn('Variant gallery mapping has no valid product media; trying the next fallback.', warningKey);
            this.lastInvalidMappingWarning = warningKey;
          }
        }

        return { mediaIds: null, signature: 'fallback' };
      }

      applyMediaSet(mediaIds, signature) {
        const mediaById = new Map(this.canonicalMedia.map((node) => [String(node.dataset.mediaId), node]));
        const thumbnailById = new Map(
          this.canonicalThumbnails.map((node) => [String(node.dataset.target), node])
        );
        const orderedIds = mediaIds || [...mediaById.keys()];
        const mediaNodes = orderedIds.map((id) => mediaById.get(String(id))).filter(Boolean);
        const thumbnailNodes = orderedIds.map((id) => thumbnailById.get(String(id))).filter(Boolean);

        if (!mediaNodes.length) return;

        this.destroySlider();
        this.destroyImageZoom();

        this.elements.mediaList.replaceChildren(
          ...mediaNodes.map((source, index) => {
            const node = source.cloneNode(true);
            node.dataset.mediaIndex = String(index);
            node.hidden = false;
            node.removeAttribute('aria-hidden');
            const zoomButton = node.querySelector('.product__media-zoom');
            if (zoomButton) zoomButton.dataset.mediaIndex = String(index);
            return node;
          })
        );

        if (this.elements.thumbnailList) {
          this.elements.thumbnailList.replaceChildren(
            ...thumbnailNodes.map((source, index) => {
              const node = source.cloneNode(true);
              node.dataset.mediaPosition = String(index + 1);
              node.classList.remove('swiper-slide-thumb-active');
              node.removeAttribute('aria-current');
              return node;
            })
          );
        }

        this.activeMediaCount = mediaNodes.length;
        this.onlyImage = this.activeMediaCount < 2;
        this.dataset.totalMediaCount = String(this.activeMediaCount);
        this.activeMediaSignature = signature;
        this.refreshElements();
        this.setSliderOptions();
        this.updateGalleryControls();
        this.updateMediaLayout();
        if (this.enableImageZoom) this.initImageZoom();
      }

      updateGalleryControls() {
        const showMultiple = this.activeMediaCount > 1;
        this.querySelectorAll('.slider-controls--group').forEach((element) => {
          element.classList.toggle('hidden', !showMultiple);
          element.toggleAttribute('hidden', !showMultiple);
          element.setAttribute('aria-hidden', String(!showMultiple));
          // The theme's grouped-slider selector is more specific than `.hidden`,
          // so an inline override is required to suppress stale Swiper counters.
          if (showMultiple) {
            element.style.removeProperty('display');
          } else {
            element.style.setProperty('display', 'none', 'important');
          }
        });
        if (this.elements.thumbnails) {
          this.elements.thumbnails.classList.toggle('hidden', !showMultiple);
          this.elements.thumbnails.toggleAttribute('hidden', !showMultiple);
          this.elements.thumbnails.setAttribute('aria-hidden', String(!showMultiple));
          if (showMultiple) {
            this.elements.thumbnails.style.removeProperty('display');
          } else {
            this.elements.thumbnails.style.setProperty('display', 'none', 'important');
          }
        }
        // Remove the reserved controls row while a mapped gallery has one item.
        if (showMultiple) {
          this.style.removeProperty('padding-bottom');
        } else {
          this.style.setProperty('padding-bottom', '0', 'important');
        }
      }

      focusMedia(mediaId) {
        if (!mediaId) return;
        const mediaItems = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        const mediaIndex = mediaItems.findIndex((node) => String(node.dataset.mediaId) === String(mediaId));
        const targetIndex = mediaIndex >= 0 ? mediaIndex : 0;

        if (this.sliderInstance?.slider && !this.sliderInstance.slider.destroyed) {
          this.sliderInstance.slider.slideToLoop(targetIndex, 0, false);
        } else if (targetIndex > 0) {
          // Preserve the theme's static-gallery behaviour: the selected featured
          // media becomes the first visible item without changing the canonical
          // media order used for later variant-gallery rebuilds.
          this.elements.mediaList?.prepend(mediaItems[targetIndex]);
        }

        const targetId = mediaItems[targetIndex]?.dataset.mediaId;
        this.querySelectorAll('.product__thumbs-item').forEach((thumbnail) => {
          const isCurrent = String(thumbnail.dataset.target) === String(targetId);
          thumbnail.toggleAttribute('aria-current', isCurrent);
        });
      }
    }
  );
}
